import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import Grid from 'components/core/Grid';
import { Box, Fade } from '@mui/material';

import { BookingSettings } from 'components/views/pages/Checkout/CheckoutStep1/components/BookingSettings';
import { PaymentSchedule } from 'components/views/pages/Checkout/CheckoutStep1/components/PaymentSchedule';
import { CheckoutLoginCreateAccountForm } from 'components/views/pages/Checkout/CheckoutUserFlow/CheckoutLoginCreateAccountForm';

import { CheckoutSummaryPriceDetails } from 'components/views/pages/Checkout/CheckoutSummary/components/CheckoutSummaryPriceDetails';
import { CheckoutCancellationPolicy } from 'components/views/pages/Checkout/CheckoutStep2/components/CheckoutCancellationPolicy';
import { useCheckoutParams } from 'components/views/pages/Checkout/hooks/useCheckoutParams';
import { useScreenWidth } from 'webHooks/useScreenWidth';
import { CheckoutStepProps } from 'pages/[propertyId]/reservenow';
import { CheckoutRequiredInfo } from './components/CheckoutRequiredInfo';
import { CheckoutPromoDetails } from './components/CheckoutPromoDetails';
import { CheckoutBoxWrapper } from '../styles';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import { CheckoutAccessDetails } from '../CheckoutStep2/components/CheckoutAccessDetails';
import { CheckoutDamageDepositSecurity } from '../CheckoutStep2/components/CheckoutDamageDepositSecurity';
import { CheckoutHouseRules } from '../CheckoutStep2/components/CheckoutHouseRules';
import { CheckoutParkingDetails } from '../CheckoutStep2/components/CheckoutParkingDetails';

export const CheckoutStep1 = (props: CheckoutStepProps) => {
  const isLoading = !props.endDate || !props.adults;
  const { isMd } = useScreenWidth();
  const { isReady } = useRouter();

  const isPetFriendly = props.checkoutData?.BOOKING.PROPERTY.IS_PET_FRIENDLY;
  const maxPartySize = props.checkoutData?.BOOKING.PARTY_SIZE.MAX;

  const ref = useRef<any>({});
  useEffect(() => {
    if (!isReady) return;
    if (!props.startDate || !props.endDate) {
      ref.current?.datesToggle?.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  const { adults } = useCheckoutParams();
  const onDatesClose = () => {
    if (adults) return;
    ref.current?.guestsToggle?.open();
  };
  const hasDamageDeposit = props.checkoutData?.BOOKING.PROPERTY.DAMAGE_DEPOSIT.IS_COLLECTED || true;

  return (
    <Fade in>
      <div>
        <Grid container>
          <BookingSettings
            ref={ref}
            {...props}
            isPetFriendly={isPetFriendly}
            maxPartySize={maxPartySize}
            isLoading={!props.checkoutData}
            onDatesClose={onDatesClose}
          />
          {isLoading || props.isInvalidDates ? null : (
            <>
              <CheckoutPromoDetails isLoading={isLoading} loginState={props.loginState} />
              {isMd ? (
                <>
                  <Grid item xs={12}>
                    <AppDivider marginVertical="16px" borderWidth="0" />
                  </Grid>
                  <CheckoutBoxWrapper>
                    <CheckoutSummaryPriceDetails {...props} hasTopAppDivider={false} hasBottomAppDivider={false} />
                  </CheckoutBoxWrapper>
                </>
              ) : null}

              <CheckoutCancellationPolicy checkoutData={props.checkoutData} />

              <Fade in>
                <div>
                  <Grid item xs={12}>
                    <AppDivider marginVertical="16px" borderWidth="0" />
                  </Grid>
                  <Grid container>
                    <CheckoutBoxWrapper mb={4}>
                      <CheckoutHouseRules checkoutData={props.checkoutData} />
                    </CheckoutBoxWrapper>
                    <CheckoutBoxWrapper mb={4}>
                      <Box>
                        <CheckoutParkingDetails checkoutData={props.checkoutData} />
                        <CheckoutAccessDetails checkoutData={props.checkoutData} />
                        {hasDamageDeposit ? <CheckoutDamageDepositSecurity checkoutData={props.checkoutData} /> : null}
                      </Box>
                    </CheckoutBoxWrapper>
                  </Grid>
                </div>
              </Fade >

              <PaymentSchedule
                checkoutData={props.checkoutData}
                paymentType={props.paymentType}
                arrivalDate={props.startDate}
              />

              {props.loginState === 'loggedin' ? (
                <CheckoutRequiredInfo {...props} />
              ) : (
                <>
                  <Grid item xs={12}>
                    <AppDivider marginVertical="16px" borderWidth="0" />
                  </Grid>
                  <CheckoutBoxWrapper>
                    <CheckoutLoginCreateAccountForm isLoading={!props.checkoutData} />
                  </CheckoutBoxWrapper>
                </>
              )}

            </>
          )}
        </Grid>
      </div>
    </Fade>
  );
};
